/**
 * The catalogue from Touchpoint's stock master. MG-01, the product half.
 *
 * The companion to `xero-contacts.ts`, and the same bargain: this is docs/09's
 * written mapping as code, so the reconciliation report is produced by the
 * same rules every run and a correction is a versioned change here rather than
 * a hand edit nobody can reproduce. It produces a *plan* - validated
 * categories, products, barcodes, prices and stock movements in the shapes
 * `product.ts`, `pricing.ts` and `quantity.ts` accept - and writes nothing.
 * Writing it into a machine's mirror is
 * `apps/desktop/scripts/import-touchpoint-stock.mjs`.
 *
 * ## The file
 *
 * Not what Xero writes. Touchpoint exports a title line, then a header, then
 * one row per stock code, separated by semicolons, in Windows-1252, with a
 * comma for the decimal point and care blurbs that run over several lines
 * inside their quotes. The title line carries the moment of the export, which
 * is where the prices' `effective_from` and the movements' `occurred_at` come
 * from: these were the figures that day, and dating them today would be this
 * module claiming something it does not know.
 *
 * ## The mapping - all forty columns
 *
 * | Touchpoint column                 | Becomes                                             |
 * | --------------------------------- | --------------------------------------------------- |
 * | Stock Code                        | `products.sku`, and the source key a re-run matches |
 * | Stock Description                 | `products.description`                              |
 * | the description's size suffix     | `products.size_grade`, and `products.unit`          |
 * | Dept Code                         | `product_categories.code`                           |
 * | Dept Description                  | `product_categories.name`                           |
 * | Ave Cost Excl                     | `products.average_cost_cents`                       |
 * | Last Cost Excl                    | `products.last_cost_cents`                          |
 * | Markup1%                          | nothing: zero on every row of this export           |
 * | Retail Price1 Excl                | `price_list_items` on tier TP1, tax exclusive       |
 * | Retail Price1 Incl                | nothing: the same price, with the tax TP1 excludes  |
 * | Retail Price2..8 Incl             | `price_list_items` on tiers TP2..TP8, tax inclusive |
 * | Stock On Hand                     | one `stock_movements` row - see below               |
 * | Shelf Qty                         | nothing: empty on every row                         |
 * | Min Order Qty, High Level Qty     | nothing: zero on every row                          |
 * | Barcode1..10                      | `product_barcodes`, every one of them               |
 * | Unit Size                         | `products.unit_size`                                |
 * | Unit Measure                      | `products.unit_measure`                             |
 * | Pack Size                         | `products.pack_size`                                |
 * | Main Supplier Code                | `products.supplier_code`, bar the placeholders      |
 * | Supplier Name                     | `products.supplier_name`                            |
 * | Sun                               | `products.sun_code`                                 |
 * | Water                             | `products.water_code`                               |
 * | Delete Flag                       | `products.active`, inverted                         |
 * | Product Benefits                  | `products.benefits`, and the dimensions below       |
 * | the blurb's "H: 5-10 cm"          | `height_min_cm`, `height_max_cm`                    |
 * | the blurb's "S: 30-60 cm"         | `spread_min_cm`, `spread_max_cm`                    |
 * | Supl.Stock Code                   | nothing: empty on every row                         |
 *
 * Nothing in the file is read and discarded without a line in the report
 * saying so, and `notImported` is that list.
 *
 * ## Three decisions worth arguing with
 *
 * **Every barcode is imported, and they are not unique.** A barcode here is a
 * price point rather than a plant: 5,234 of the 5,998 rows carrying a Barcode1
 * share it with another row, and one code sits on 202 different 14cm pots
 * because it is what the till charges for a 14cm pot. Migration 0015 moved
 * `product_barcodes_unique` onto `(product_id, barcode)` for exactly this, so
 * all of them are filed. The **primary** is the first that is unique to this
 * plant in the whole file, because "the code a scanner should read for this
 * plant" wants an answer that names one plant; where every code on a row is
 * shared, the first is primary anyway and the report counts those plants.
 *
 * **Eight tiers, named after the columns they came from, and every one of
 * them quotable.** Nothing in the export names a tier, so inventing names
 * would be worse than keeping the source's - these are real trade tiers, and
 * which customer sits on which is the client's to say on each account.
 *
 * Seven of the eight columns are quoted *including* tax and
 * `price_list_items` is read by `documentTotals`, which adds tax to a line
 * rather than extracting it from one. Storing them as exported would overcharge
 * every customer on tiers 2 to 8 by the whole rate. So they are converted to
 * tax-exclusive on the way in, and `price_tiers.tax_inclusive` is false on all
 * eight because that is what is stored.
 *
 * **The rate is read off the file, not chosen.** Touchpoint gives price 1 both
 * ways, and the ratio is 15% on every row bar four where the exclusive figure
 * is rounded to the cent. The other seven columns sit on the same grid exactly:
 * all 5,806 of `Retail Price3 Incl`, all 4,070 of price 5, all 5,762 of price 6
 * and all 5,856 of price 8 divide by 1.15 and multiply back to the cent they
 * came from. A conversion that round-trips on every row is arithmetic the file
 * already did, not a rate this module picked - and `taxBasisPoints` is an
 * option so a future export at another rate does not need a code change.
 *
 * **Stock on hand becomes one movement, not a column.** CLAUDE.md: stock is a
 * ledger and a client never writes an absolute. So each non-zero figure is a
 * single `stocktake` movement dated to the export, which is the only shape
 * that is both true to the file and true to the model. It is worth saying
 * plainly, and the report does: most of those figures are negative and the
 * derived on-hand will be negative with them, because that is what Touchpoint
 * holds. A real stocktake is what replaces them.
 */
import { validateBarcodeEntry, validateCategory, validateProduct, } from './product.js';
import { roundHalfAwayFromZero } from './money.js';
import { validatePrice } from './pricing.js';
import { formatQuantityExact, parseQuantity } from './quantity.js';
import { parseCsv } from './xero-contacts.js';
// ---------------------------------------------------------------------------
// The file, as bytes
// ---------------------------------------------------------------------------
/**
 * The 32 code points Windows-1252 puts where Latin-1 keeps control codes.
 *
 * Touchpoint writes the care blurbs with real typography - "H: 5-10 cm" uses
 * an en dash, 0x96 - and Node has no Windows-1252 decoder it can promise is
 * built in. Reading the file as `latin1` is exact for every other byte, so
 * this repairs the one range that differs. A table rather than a dependency,
 * and a table rather than `TextDecoder`, because the failure mode of guessing
 * is a plant description with a replacement character printed on a label.
 *
 * Numbers rather than characters so the source stays ASCII, and because five
 * of these are undefined in Windows-1252 and keep their Latin-1 value.
 */
// prettier-ignore
const WINDOWS_1252_HIGH = [
    0x20ac, 0x0081, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021,
    0x02c6, 0x2030, 0x0160, 0x2039, 0x0152, 0x008d, 0x017d, 0x008f,
    0x0090, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014,
    0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x009d, 0x017e, 0x0178,
];
/** Repair a string read as `latin1` into the Windows-1252 it really was. */
export function fromWindows1252(latin1) {
    return latin1.replace(/[\u0080-\u009F]/g, (char) => {
        const code = WINDOWS_1252_HIGH[char.charCodeAt(0) - 0x80];
        return code === undefined ? char : String.fromCharCode(code);
    });
}
/**
 * The header row, and the rows under it.
 *
 * Touchpoint puts a title line above the header - the nursery's name, the
 * words STOCK MASTER FILE and the moment of the export - so the header is
 * found by looking for the column every version of this file has rather than
 * by counting lines, which would break the first time somebody exports it
 * with a different heading.
 */
function readStockRecords(text) {
    const rows = parseCsv(text, ';');
    const headerIndex = rows.findIndex((row) => row.some((cell) => cell.trim() === 'Stock Code'));
    if (headerIndex === -1)
        return { records: [], headerLine: 0 };
    const names = (rows[headerIndex] ?? []).map((name) => name.trim());
    const records = rows
        .slice(headerIndex + 1)
        .filter((row) => row.some((cell) => cell.trim() !== ''))
        .map((row) => {
        const record = {};
        names.forEach((name, column) => {
            record[name] = row[column] ?? '';
        });
        return record;
    });
    const title = rows.slice(0, headerIndex).flat().join(' ');
    const stamp = /(\d{4})[/-](\d{2})[/-](\d{2})/.exec(title);
    const exportedOn = stamp === null
        ? undefined
        : `${stamp[1] ?? ''}-${stamp[2] ?? ''}-${stamp[3] ?? ''}`;
    return {
        records,
        headerLine: headerIndex + 1,
        ...(exportedOn === undefined ? {} : { exportedOn }),
    };
}
/**
 * The eight price columns, as eight tiers.
 *
 * Named for where they came from, because nothing in the export names them and
 * a guessed name on a price list is how a customer is quoted the wrong number.
 */
const TIERS = [
    {
        code: 'TP1',
        name: 'Touchpoint price 1',
        description: "Touchpoint's Retail Price1 Excl, as exported. The one column given " +
            'exclusive of tax, and so the one the application can quote from today. ' +
            'Rename it once the trade tiers are named (Q9).',
        sourceIncludedTax: false,
        sourceColumn: 'Retail Price1 Excl',
        column: 'Retail Price1 Excl',
    },
    ...[2, 3, 4, 5, 6, 7, 8].map((n) => ({
        code: `TP${String(n)}`,
        name: `Touchpoint price ${String(n)}`,
        description: `Touchpoint's Retail Price${String(n)} Incl, converted to a tax-exclusive ` +
            'price at the rate the export itself states. A line adds tax to this ' +
            'figure. Rename this tier once the trade tiers are named (Q9).',
        sourceIncludedTax: true,
        sourceColumn: `Retail Price${String(n)} Incl`,
        column: `Retail Price${String(n)} Incl`,
    })),
];
/** 15%, which is what this export states. `StockImportOptions` says why. */
const DEFAULT_TAX_BASIS_POINTS = 1500;
/**
 * A tax-inclusive amount, as the tax-exclusive one it was built from.
 *
 * Integer arithmetic throughout and rounded half away from zero, the way every
 * other amount in this application is, so the result is the cent Touchpoint
 * started from rather than a float that lands near it.
 */
function withoutTax(cents, basisPoints) {
    return Number(roundHalfAwayFromZero(BigInt(cents) * 10000n, BigInt(10_000 + basisPoints)));
}
function clean(value) {
    const trimmed = (value ?? '').trim();
    return trimmed === '' ? undefined : trimmed;
}
/**
 * The punctuation a grower typed, as the characters a label can carry.
 *
 * `packages/labels` refuses anything outside printable ASCII before a byte
 * reaches a printer - deliberately, because the code page this site's Argox is
 * set to has not been confirmed (docs/00) and a guess is discovered on a
 * pallet. Touchpoint's blurbs are full of real typography: 209 of them write
 * "H: 60-90 cm" with an en dash, and a label binding one would refuse to print
 * rather than print it wrong.
 *
 * So the repertoire is applied here, where the catalogue is filled, rather than
 * at the printer where it would be a refusal or in the renderer where the screen
 * and the label would disagree about what a plant says. If the site visit
 * confirms a code page that carries more, this table shrinks and the import is
 * run again.
 */
const PUNCTUATION = new Map([
    ['\u2013', '-'],
    ['\u2014', '-'],
    ['\u2212', '-'],
    ['\u2018', "'"],
    ['\u2019', "'"],
    ['\u201B', "'"],
    ['\u201C', '"'],
    ['\u201D', '"'],
    ['\u2026', '...'],
    ['\u00D7', 'x'],
    ['\u00B0', ' deg'],
    ['\u00A0', ' '],
    ['\u2022', '-'],
    ['\u00BD', '1/2'],
]);
const PUNCTUATED = /[\u00A0\u00B0\u00BD\u00D7\u2013\u2014\u2018\u2019\u201B\u201C\u201D\u2022\u2026\u2212]/g;
function toLabelText(value) {
    const mapped = value
        .replace(PUNCTUATED, (char) => PUNCTUATION.get(char) ?? char)
        // An accented letter becomes its unaccented one rather than vanishing:
        // "Aloe" is a plant somebody can look up, and "Alo" is not.
        .normalize('NFKD')
        .replace(/[\u0300-\u036F]/g, '');
    // Whatever is left that a printer here cannot carry becomes a space, so a
    // word does not silently join the next one. The report counts the rows.
    return mapped
        .replace(/[^ -~]/g, ' ')
        .replace(/ {2,}/g, ' ')
        .trim();
}
/** A blurb that ran over several lines in its quotes is one paragraph here. */
function flatten(value) {
    const text = clean(value);
    return text === undefined ? undefined : text.replace(/\s+/g, ' ').trim();
}
/** Supplier codes that mean "nobody filled this in". */
const SUPPLIER_PLACEHOLDERS = new Set(['88888888', '00000000', 'Not Found']);
/**
 * An amount, as integer cents.
 *
 * `parseAmount` is the application's parser and refuses more than two
 * decimals, which is right for a price somebody typed and wrong for
 * Touchpoint's costs: it keeps three, and `5,196` is a real average cost. So
 * this rounds to the cent and says that it did, and the report counts them.
 */
function centsOf(raw) {
    const found = /^(-?)(\d+)(?:\.(\d+))?$/.exec(raw.trim().replace(',', '.'));
    if (found === null)
        return undefined;
    const [, sign = '', whole = '0', fraction = ''] = found;
    const thousandths = Number(whole) * 1000 + Number(fraction.padEnd(3, '0').slice(0, 3));
    const cents = Math.round(thousandths / 10);
    return { cents: sign === '-' ? -cents : cents, rounded: fraction.length > 2 };
}
/** A whole number, or nothing. Touchpoint writes `0` where it means "unset". */
function countOf(raw) {
    const text = clean(raw);
    if (text === undefined)
        return undefined;
    const value = Number.parseInt(text, 10);
    if (!Number.isSafeInteger(value) || value === 0)
        return undefined;
    return value;
}
/**
 * The size, as the description carries it.
 *
 * Every plant in this catalogue ends with what it is grown in - `14cm`,
 * `6pack`, `200 Tray`, `4kg` - and that suffix is the pot size the label
 * prints and the only place in the file it is reliable. `Unit Size` and
 * `Unit Measure` hold the same fact and disagree with the description on 623
 * rows, spelling one pot four ways ("14 cm", "14cm", "cm", "Other"), so the
 * description decides `size_grade` and both raw columns are kept beside it.
 *
 * The optional trailing noun is for "SMF Angelonia 14cm Pot", where somebody
 * typed what the 14cm was.
 */
const SIZE = new RegExp(String.raw `(\d+(?:[.,]\d+)?)\s*(cm|mm|kg|g|ml|l|lt|ltr)` +
    String.raw `(?:\s+(?:pot|pots|bowl|basket|bag|hb|tc|stk))?\s*$` +
    String.raw `|(\d+)\s*(pack|pk)(?:\s+\w+)?\s*$` +
    String.raw `|(\d+)\s*(tray|trays)\s*$`, 'i');
function sizeOf(description) {
    const found = SIZE.exec(description);
    if (found === null)
        return undefined;
    const [, measured, measure, packed, , trayed] = found;
    if (measured !== undefined && measure !== undefined) {
        return {
            sizeGrade: `${measured.replace(',', '.')}${measure.toLowerCase()}`,
            unit: 'each',
        };
    }
    if (packed !== undefined)
        return { sizeGrade: `${packed} pack`, unit: 'pack' };
    if (trayed !== undefined)
        return { sizeGrade: `${trayed} tray`, unit: 'tray' };
    return undefined;
}
/**
 * Height and spread, out of the blurb that states them.
 *
 * Touchpoint has no column for either; a grower wrote them into the care text
 * in one of two shapes - "H: 5-10 cm | S: 30-60 cm" on most rows, and
 * "35cm x 20cm" on the rest. Both are read, metres become centimetres, and a
 * single figure fills the minimum and the maximum, because "H: 5 cm" is a
 * plant that reaches five centimetres rather than a range whose top nobody
 * wrote down.
 *
 * The dash may be a hyphen or the en dash `fromWindows1252` restores.
 */
const LABELLED_DIMENSION = /\b([HS])\s*:\s*(\d+(?:[.,]\d+)?)\s*(?:[-\u2013\u2014]\s*(\d+(?:[.,]\d+)?))?\s*(cm|m)\b/gi;
const CROSSED_DIMENSION = /(\d+(?:[.,]\d+)?)\s*(cm|m)\s*[x\u00D7]\s*(\d+(?:[.,]\d+)?)\s*(cm|m)\b/i;
function centimetres(value, unit) {
    const magnitude = Number(value.replace(',', '.'));
    return Math.round(unit.toLowerCase() === 'm' ? magnitude * 100 : magnitude);
}
function dimensionsOf(blurb) {
    if (blurb === undefined)
        return {};
    const found = new Map();
    LABELLED_DIMENSION.lastIndex = 0;
    let match = LABELLED_DIMENSION.exec(blurb);
    while (match !== null) {
        const [, axis = '', low = '', high, unit = 'cm'] = match;
        const least = centimetres(low, unit);
        found.set(axis.toUpperCase(), [
            least,
            high === undefined ? least : centimetres(high, unit),
        ]);
        match = LABELLED_DIMENSION.exec(blurb);
    }
    if (found.size === 0) {
        const crossed = CROSSED_DIMENSION.exec(blurb);
        if (crossed !== null) {
            const [, tall = '', tallUnit = 'cm', wide = '', wideUnit = 'cm'] = crossed;
            const height = centimetres(tall, tallUnit);
            const spread = centimetres(wide, wideUnit);
            found.set('H', [height, height]);
            found.set('S', [spread, spread]);
        }
    }
    const height = found.get('H');
    const spread = found.get('S');
    return {
        ...(height === undefined ? {} : { heightMinCm: height[0], heightMaxCm: height[1] }),
        ...(spread === undefined ? {} : { spreadMinCm: spread[0], spreadMaxCm: spread[1] }),
    };
}
/** Two descriptions that differ only in case, spacing or punctuation. */
function samePlantKey(description) {
    return description.toLowerCase().replace(/[^a-z0-9]/g, '');
}
/** The characters Code 39 can carry; anything else has to be Code 128. */
const CODE39_ALPHABET = /^[0-9A-Z. $/+%-]+$/;
/**
 * What symbology a payload could be, by its shape alone.
 *
 * Thirteen digits is an EAN-13 and is checked as one. Eight digits is an
 * EAN-8, which `product_barcodes.symbology` does not offer, so it is filed as
 * Code 128 - the payload is exact either way and Code 128 is what a printer
 * here would emit for it. Everything else is judged by its alphabet.
 */
function symbologyOf(payload) {
    if (/^\d{13}$/.test(payload))
        return 'EAN13';
    if (/^\d{8}$/.test(payload))
        return 'CODE128';
    if (/^\d{1,7}$/.test(payload))
        return undefined;
    if (CODE39_ALPHABET.test(payload.toUpperCase()))
        return 'CODE39';
    return 'CODE128';
}
/** Retail codes first, the article code last: a dock scans the retail one. */
const BARCODE_COLUMNS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
/**
 * Read Touchpoint's stock master and plan the catalogue it becomes.
 *
 * Nothing is written, and nothing is dropped quietly: a row that cannot become
 * a product is a skip with a reason and a line number, and a column that could
 * have been imported and was not is in `notImported` with the count of rows it
 * would have filled.
 */
export function planTouchpointStock(text, options) {
    const { records, headerLine, exportedOn } = readStockRecords(text);
    const effectiveFrom = exportedOn ?? options.today?.() ?? new Date().toISOString().slice(0, 10);
    const taxBasisPoints = options.taxBasisPoints ?? DEFAULT_TAX_BASIS_POINTS;
    // Every barcode in the file, counted by the number of plants carrying it,
    // before any row is judged: a value is only this plant's own if it is on no
    // other row, and that cannot be known from one row at a time.
    //
    // Counted once per row rather than once per column, because a row that
    // lists the same code in two of its ten columns is one plant carrying it,
    // and counting it twice would make a code nobody else has look shared.
    const seen = new Map();
    for (const record of records) {
        const onThisRow = new Set();
        for (const column of BARCODE_COLUMNS) {
            const value = clean(record[`Barcode${String(column)}`]);
            if (value !== undefined)
                onThisRow.add(value);
        }
        for (const value of onThisRow)
            seen.set(value, (seen.get(value) ?? 0) + 1);
    }
    const categories = new Map();
    const products = [];
    const skipped = [];
    const keys = new Map();
    let barcodeCount = 0;
    let priceCount = 0;
    let movementCount = 0;
    let sharedPrimary = 0;
    let noBarcode = 0;
    let sizeMissing = 0;
    let noPrice = 0;
    let pricesConverted = 0;
    let costsRounded = 0;
    let textNormalised = 0;
    records.forEach((record, index) => {
        const line = headerLine + index + 1;
        const sku = clean(record['Stock Code']);
        const raw = clean(record['Stock Description']);
        const description = raw === undefined ? undefined : toLabelText(raw);
        if (sku === undefined || description === undefined) {
            skipped.push({ line, reason: 'no stock code or no description' });
            return;
        }
        const remarks = [];
        // The department, filed once however many plants name it.
        const deptCode = clean(record['Dept Code']);
        const deptName = clean(record['Dept Description']);
        if (deptCode !== undefined && deptName !== undefined && !categories.has(deptCode)) {
            const checked = validateCategory({
                name: deptName,
                code: deptCode,
                sortOrder: Number.parseInt(deptCode, 10) || 0,
            });
            if (checked.ok) {
                categories.set(deptCode, { sourceKey: deptCode, category: checked.value });
            }
        }
        const sized = sizeOf(description);
        if (sized === undefined) {
            sizeMissing += 1;
            remarks.push('No size in the description, so the plant has no size or grade.');
        }
        const written = flatten(record['Product Benefits']);
        const benefits = written === undefined ? undefined : toLabelText(written);
        if ((written !== undefined && written !== benefits) || raw !== description) {
            textNormalised += 1;
        }
        const supplierCode = clean(record['Main Supplier Code']);
        const supplierName = clean(record['Supplier Name']);
        const unitSize = clean(record['Unit Size']);
        const unitMeasure = clean(record['Unit Measure']);
        const sunCode = countOf(record['Sun']);
        const waterCode = countOf(record['Water']);
        const packSize = countOf(record['Pack Size']);
        const average = centsOf(record['Ave Cost Excl'] ?? '');
        const last = centsOf(record['Last Cost Excl'] ?? '');
        if (average?.rounded === true || last?.rounded === true) {
            costsRounded += 1;
        }
        const product = validateProduct({
            sku,
            description,
            // Touchpoint's own flag, inverted. Blank on every row of this export,
            // which is why the report says the active set cannot be derived.
            active: clean(record['Delete Flag']) === undefined,
            ...(sized === undefined ? {} : { unit: sized.unit, sizeGrade: sized.sizeGrade }),
            ...(benefits === undefined ? {} : { benefits }),
            ...dimensionsOf(benefits),
            ...(sunCode === undefined ? {} : { sunCode }),
            ...(waterCode === undefined ? {} : { waterCode }),
            ...(unitSize === undefined ? {} : { unitSize }),
            ...(unitMeasure === undefined ? {} : { unitMeasure }),
            ...(packSize === undefined ? {} : { packSize }),
            ...(supplierCode === undefined || SUPPLIER_PLACEHOLDERS.has(supplierCode)
                ? {}
                : { supplierCode }),
            ...(supplierName === undefined ? {} : { supplierName }),
            ...(average === undefined || average.cents === 0
                ? {}
                : { averageCostCents: average.cents }),
            ...(last === undefined || last.cents === 0 ? {} : { lastCostCents: last.cents }),
        });
        if (!product.ok) {
            skipped.push({
                line,
                reason: `${sku} ${description}: ${product.error.map((problem) => problem.message).join(' ')}`,
            });
            return;
        }
        // Every barcode on the row. The primary is the first that names this plant
        // and no other; where they are all shared, the first is primary anyway and
        // the report counts the plants in that position.
        const candidates = [];
        for (const column of BARCODE_COLUMNS) {
            const value = clean(record[`Barcode${String(column)}`]);
            if (value === undefined)
                continue;
            if (candidates.some((one) => one.barcode.barcode === value))
                continue;
            const symbology = symbologyOf(value);
            if (symbology === undefined) {
                remarks.push(`Barcode "${value}" is too short to be one; left off.`);
                continue;
            }
            const checked = validateBarcodeEntry({ barcode: value, symbology });
            if (!checked.ok) {
                remarks.push(`Barcode "${value}" left off: ${checked.error[0]?.message ?? ''}`);
                continue;
            }
            if (!options.checkBarcode(symbology, value)) {
                remarks.push(`Barcode "${value}" fails its check digit; left off.`);
                continue;
            }
            candidates.push({ barcode: checked.value, shared: (seen.get(value) ?? 0) > 1 });
        }
        const primaryAt = candidates.findIndex((one) => !one.shared);
        const first = candidates[primaryAt];
        const ordered = primaryAt <= 0 || first === undefined
            ? candidates
            : [first, ...candidates.filter((_, at) => at !== primaryAt)];
        const barcodes = ordered.map((one, at) => ({
            ...one.barcode,
            isPrimary: at === 0,
        }));
        barcodeCount += barcodes.length;
        if (barcodes.length === 0) {
            noBarcode += 1;
            remarks.push('No barcode on this row that could be filed.');
        }
        else if (primaryAt === -1) {
            sharedPrimary += 1;
            remarks.push('Every barcode here is a price point shared with other plants, so the ' +
                'primary names more than one.');
        }
        // All eight price columns, each onto its own tier.
        const prices = [];
        for (const tier of TIERS) {
            const raw = clean(record[tier.column]);
            if (raw === undefined)
                continue;
            const amount = centsOf(raw);
            if (amount === undefined) {
                remarks.push(`${tier.column} "${raw}" is not an amount, so no price was filed.`);
                continue;
            }
            // A zero here is an empty cell, not a gift: a quote line that silently
            // reads R0.00 is worse than one that says it has no price.
            if (amount.cents === 0)
                continue;
            // Seven of the eight columns are quoted including tax, and a line adds
            // tax to what it reads. Storing them as exported would charge it twice.
            const priceCents = tier.sourceIncludedTax
                ? withoutTax(amount.cents, taxBasisPoints)
                : amount.cents;
            if (tier.sourceIncludedTax)
                pricesConverted += 1;
            const checked = validatePrice({ priceCents, effectiveFrom });
            if (!checked.ok) {
                remarks.push(`${tier.column} refused: ${checked.error.map((problem) => problem.message).join(' ')}`);
                continue;
            }
            prices.push({ tierCode: tier.code, price: checked.value });
        }
        priceCount += prices.length;
        if (prices.length === 0) {
            noPrice += 1;
            remarks.push('No price on any of the eight columns.');
        }
        // The on-hand figure, as the one movement it can honestly be.
        let movement;
        const onHand = clean(record['Stock On Hand']);
        if (onHand !== undefined) {
            const counted = parseQuantity(onHand.replace(',', '.'));
            if (!counted.ok) {
                remarks.push(`Stock on hand "${onHand}" is not a quantity; no movement filed.`);
            }
            else if (counted.value !== 0) {
                movement = {
                    quantityDelta: formatQuantityExact(counted.value),
                    reason: 'stocktake',
                };
                movementCount += 1;
            }
        }
        const key = samePlantKey(description);
        keys.set(key, [...(keys.get(key) ?? []), `${sku} ${description}`]);
        products.push({
            sourceKey: sku,
            product: product.value,
            barcodes,
            prices,
            remarks,
            ...(deptCode === undefined ? {} : { categoryKey: deptCode }),
            ...(movement === undefined ? {} : { movement }),
        });
    });
    const filled = (column) => records.filter((record) => {
        const value = clean(record[column]);
        return value !== undefined && value !== '0';
    }).length;
    const notImported = [
        {
            column: 'Retail Price1 Incl',
            rows: filled('Retail Price1 Incl'),
            why: 'The same price as Retail Price1 Excl with the tax added. TP1 holds the ' +
                'exclusive figure and the application adds tax to a line, so importing ' +
                'both would be one price in two places, free to disagree.',
        },
        {
            column: 'Markup1%',
            rows: filled('Markup1%'),
            why: 'Zero on every row of this export.',
        },
        {
            column: 'Supl.Stock Code, Shelf Qty, Min Order Qty, High Level Qty',
            rows: filled('Supl.Stock Code') +
                filled('Shelf Qty') +
                filled('Min Order Qty') +
                filled('High Level Qty'),
            why: 'Empty or zero on every row of this export.',
        },
        {
            column: 'Delete Flag',
            rows: filled('Delete Flag'),
            why: 'Blank on every row, so every plant is imported active. With no ' +
                'last-sold date either, the active set cannot be derived from this file: ' +
                "docs/09 step 5 needs a sales export or Touchpoint's own filter.",
        },
    ];
    return {
        categories: [...categories.values()].sort((a, b) => a.category.sortOrder - b.category.sortOrder),
        products,
        skipped,
        tiers: TIERS.map(({ column: _column, ...tier }) => tier),
        effectiveFrom,
        duplicateDescriptions: [...keys.values()].filter((names) => names.length > 1),
        notImported,
        taxBasisPoints,
        counts: {
            rows: records.length,
            products: products.length,
            categories: categories.size,
            barcodes: barcodeCount,
            prices: priceCount,
            movements: movementCount,
            sharedPrimary,
            noBarcode,
            sizeMissing,
            noPrice,
            pricesConverted,
            costsRounded,
            textNormalised,
            skipped: skipped.length,
        },
    };
}
// ---------------------------------------------------------------------------
// The report
// ---------------------------------------------------------------------------
/**
 * The reconciliation report docs/09 asks for, as Markdown a person can read
 * and sign. Counts first, then what was left out and why, then the decisions
 * this module took by plant - so each can be checked against the source file.
 *
 * The remarks are capped. A report with 5,223 identical lines about shared
 * barcodes is one nobody reads, and the count above it is the same fact.
 */
export function describeStockPlan(plan, source, limit = 60) {
    const { counts } = plan;
    const lines = [
        `# Catalogue from Touchpoint: ${source}`,
        '',
        `Exported ${plan.effectiveFrom}, which is the day the prices and the stock`,
        'movements are dated from.',
        '',
        '| What | Count |',
        '| --- | ---: |',
        `| Rows in the file | ${String(counts.rows)} |`,
        `| Plants planned | ${String(counts.products)} |`,
        `| Categories | ${String(counts.categories)} |`,
        `| Barcodes filed | ${String(counts.barcodes)} |`,
        `| Plants whose primary barcode is shared | ${String(counts.sharedPrimary)} |`,
        `| Plants with no barcode at all | ${String(counts.noBarcode)} |`,
        `| Prices filed | ${String(counts.prices)} |`,
        `| Prices converted from tax-inclusive | ${String(counts.pricesConverted)} |`,
        `| Plants with no price on any tier | ${String(counts.noPrice)} |`,
        `| Stock movements | ${String(counts.movements)} |`,
        `| Plants with no size in the description | ${String(counts.sizeMissing)} |`,
        `| Plants whose cost was rounded to the cent | ${String(counts.costsRounded)} |`,
        `| Plants whose text was put into the label repertoire | ${String(counts.textNormalised)} |`,
        `| Rows skipped | ${String(counts.skipped)} |`,
        '',
        '## The price tiers',
        '',
        'One per price column, named for where it came from. Nothing in the export',
        'names a tier, and a guessed name on a price list is how a customer is quoted',
        'the wrong number. Q9 decides which survive and what they are called.',
        '',
        '| Tier | Source column | Source quoted | Prices |',
        '| --- | --- | --- | ---: |',
    ];
    for (const tier of plan.tiers) {
        const filled = plan.products.filter((product) => product.prices.some((price) => price.tierCode === tier.code)).length;
        lines.push(`| ${tier.code} ${tier.name} | ${tier.sourceColumn} | ` +
            `${tier.sourceIncludedTax ? 'including tax' : 'excluding tax'} | ${String(filled)} |`);
    }
    lines.push('', `**Every tier holds a tax-exclusive price**, so any of them can price a line:`, 'a line adds tax to what it reads rather than extracting it. The seven', `columns quoted including tax were converted at ${(plan.taxBasisPoints / 100).toFixed(2)}%,`, 'which is the rate this export states rather than one chosen here - price 1', 'is given both ways, and every inclusive figure in the file divides by that', 'rate and multiplies back to the cent it came from.', '', '**What is still open is which customer sits on which tier.** Nothing in', 'either export says, so every account is on no tier until somebody sets it', 'on the account. A customer with no tier has no price and the screen says so', 'rather than guessing one.', '', '## Stock on hand', '', `${String(counts.movements)} plants had a non-zero figure, and each became one`, '`stocktake` movement dated to the export, because stock here is a ledger and', 'never an absolute a client writes.', '', '**Read this before trusting an on-hand figure on screen.** Most of the', "source's figures are negative, so the derived on-hand is negative with them.", 'That is what Touchpoint holds, not what the import did to it. A real', 'stocktake is what replaces them.', '', '## The characters a label can carry', '', 'A printer here refuses anything outside printable ASCII, because the code', "page this site's Argox is set to has not been confirmed. Touchpoint's blurbs", 'are written with real typography - an en dash in "H: 60-90 cm" is the common', 'one - so that punctuation is mapped to its ASCII equivalent on the way in,', 'and an accented letter becomes its unaccented one rather than vanishing.', 'Without this a label binding a care blurb would refuse to print rather than', 'print it wrong. If the site visit confirms a wider code page, this shrinks', 'and the import is run again.', '', '## Read, and deliberately not imported', '', '| Column | Rows with a value | Why not |', '| --- | ---: | --- |');
    for (const left of plan.notImported) {
        lines.push(`| ${left.column} | ${String(left.rows)} | ${left.why} |`);
    }
    lines.push('');
    lines.push('## Categories', '', '| Code | Department | Plants |', '| --- | --- | ---: |');
    for (const category of plan.categories) {
        const count = plan.products.filter((product) => product.categoryKey === category.sourceKey).length;
        lines.push(`| ${category.category.code ?? ''} | ${category.category.name} | ${String(count)} |`);
    }
    lines.push('');
    if (plan.skipped.length > 0) {
        lines.push('## Skipped', '');
        for (const row of plan.skipped) {
            lines.push(`- Line ${String(row.line)}: ${row.reason}`);
        }
        lines.push('');
    }
    if (plan.duplicateDescriptions.length > 0) {
        lines.push('## Descriptions that read as the same plant twice', '', 'All are imported - the stock codes differ, so they are different rows in', 'Touchpoint too. Decide which stays and retire the other on its record.', '');
        for (const names of plan.duplicateDescriptions.slice(0, limit)) {
            lines.push(`- ${names.join('  |  ')}`);
        }
        if (plan.duplicateDescriptions.length > limit) {
            lines.push(`- ...and ${String(plan.duplicateDescriptions.length - limit)} more.`);
        }
        lines.push('');
    }
    const noSize = plan.products.filter((product) => product.product.sizeGrade === undefined);
    if (noSize.length > 0) {
        lines.push('## Plants with no size', '', 'The description does not end in a pot, a pack or a tray, so there is no', 'size or grade on the record and a label that prints one will leave it off.', '');
        for (const product of noSize.slice(0, limit)) {
            lines.push(`- ${product.sourceKey}  ${product.product.description}`);
        }
        if (noSize.length > limit) {
            lines.push(`- ...and ${String(noSize.length - limit)} more.`);
        }
        lines.push('');
    }
    const routine = ['Every barcode here', 'No size in the description', 'No barcode on'];
    const odd = plan.products.filter((product) => product.remarks.some((remark) => !routine.some((one) => remark.startsWith(one))));
    if (odd.length > 0) {
        lines.push('## Remarks by plant', '');
        for (const product of odd.slice(0, limit)) {
            lines.push(`- **${product.sourceKey}** ${product.product.description}`);
            for (const remark of product.remarks)
                lines.push(`  - ${remark}`);
        }
        if (odd.length > limit) {
            lines.push(`- ...and ${String(odd.length - limit)} more plants with remarks.`);
        }
        lines.push('');
    }
    return lines.join('\n');
}
//# sourceMappingURL=touchpoint-stock.js.map